import React from "react";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import { getPassportEntries } from "../../../../context/Adviser/Utils/passportImages";
import { useNavigate } from "react-router-dom";
import {
  safe,
  asList,
  formatAddress,
  formatUKPhone,
  initialsFrom,
  cssColor,
  fmtMaybeDate,
  fmtMaybeMonthYear,
  statusColor,
  QUAL_GROUPS,
  pickTruthy,
  fmtDate,
} from "../../Utils/AdviserFormatters";
import {
  Chip,
  PostCard,
  Row,
} from "../HelperFunctions/HelperFunctions";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";


export const AdviserDetails = ({
  adviserKey,
  details,
  loading,
  header,
  canEdit,
}) => {
  const navigate = useNavigate();
  const adviserAdmins = asList(details?.adviserDoc?.admins).filter(Boolean);

  const handleEdit = () => {
    const key = adviserKey || header.email;
    if (!key) return;

    navigate(`/advisers/${encodeURIComponent(key)}/edit`, {
      state: {
        header,
        details,
        returnTo: `/advisers/${encodeURIComponent(key)}?tab=adviserDetails`,
      },
    });
  };

  return (
    <div className="smFeed">
      {loading ? (
        <div className="smLoading Content-Small">Loading profile…</div>
      ) : (
        <>
          {canEdit ? (
            <div className="smEditActions">
              <div>
                <div className="FP_Body_Semibold">Adviser Details</div>
                <p className="Content-Small muted">
                  Contact, personal and application details.
                </p>
              </div>
              <SecondaryButton label="Edit Details" onClick={handleEdit} />
            </div>
          ) : null}

          <PostCard title="Contact">
            <div className="smGrid2">
              <Row
                label="Email"
                value={details.personalDetails?.emailAddress || header.email}
                href={`mailto:${details.personalDetails?.emailAddress || header.email}`}
              />
              <Row
                label="Mobile"
                value={formatUKPhone(details.personalDetails?.mobileTelephone)}
                href={`tel:${formatUKPhone(details.personalDetails?.mobileTelephone)}`}
              />
              <Row
                label="Home"
                value={formatUKPhone(details.personalDetails?.homeTelephone)}
                href={`tel:${formatUKPhone(details.personalDetails?.homeTelephone)}`}
              />
              <Row
                label="Office"
                value={formatUKPhone(details.personalDetails?.officeTelephone)}
                href={`tel:${formatUKPhone(details.personalDetails?.officeTelephone)}`}
              />
            </div>
          </PostCard>

          <PostCard title="Business address">
            <Row
              label="Address"
              value={formatAddress(header.businessAddress)}
            />
          </PostCard>

          <PostCard title="Current address">
            <Row
              label="Current address"
              value={details.personalDetails?.currentAddress}
            />
            <div className="smGrid2">
              <Row label="Town" value={details.personalDetails?.currentTown} />
              <Row
                label="County"
                value={details.personalDetails?.currentCounty}
              />
              <Row
                label="Postcode"
                value={details.personalDetails?.currentPostcode}
              />
              <Row
                label="Moving in date"
                value={details.personalDetails?.movingInDate}
                kind="monthYear"
              />
            </div>
          </PostCard>

          <PostCard title="Personal details">
            <div className="smGrid2">
              <Row
                label="First name"
                value={details.personalDetails?.forename}
              />
              <Row label="Last name" value={details.personalDetails?.surname} />
              <Row
                label="Preferred name"
                value={
                  details.personalDetails?.preferredName ||
                  details.adviserDoc?.preferredName
                }
              />
              <Row
                label="Date of birth"
                value={fmtDate(details.personalDetails?.dob)}
                kind="date"
              />
              <Row
                label="Nationality at birth"
                value={details.personalDetails?.nationalityAtBirth}
              />
              <Row
                label="Town of birth"
                value={details.personalDetails?.townOfBirth}
              />
              <Row
                label="Country of birth"
                value={details.personalDetails?.countryOfBirth}
              />
              <Row
                label="Name change date"
                value={details.personalDetails?.nameChangeDate}
                kind="monthYear"
              />
              <Row
                label="Lived in UK since"
                value={details.personalDetails?.durationInUk}
                kind="monthYear"
              />
            </div>
          </PostCard>

          <PostCard title="Application details">
            <div className="smGrid2">
              <Row
                label="Applying as"
                value={
                  asList(details.applicationDetails?.applyingAs).join(", ") ||
                  "-"
                }
              />
              <Row
                label="Applying to join"
                value={details.applicationDetails?.applyingToJoin}
              />
              <Row
                label="Date appointed"
                value={fmtDate(details.adviserDoc?.dateAppointed)}
                kind="date"
              />
              <Row
                label="Date left"
                value={fmtDate(details.adviserDoc?.dateLeft)}
                kind="date"
              />
              <Row
                label="Business permissions"
                value={
                  asList(details.applicationDetails?.businessAreas).join(
                    ", ",
                  ) || "-"
                }
              />
              <Row
                label="AR business permissions"
                value={
                  asList(details.applicationDetails?.ARBusinessAreas).join(
                    ", ",
                  ) || "-"
                }
              />
              <Row
                label="Trading style"
                value={details.applicationDetails?.tradingStyle}
              />
              <Row
                label="Referred business"
                value={
                  details.applicationDetails?.referredBusiness === true
                    ? "Yes"
                    : details.applicationDetails?.referredBusiness === false
                      ? "No"
                      : "-"
                }
              />
            </div>
          </PostCard>

          <PostCard title="Passport Details">
            <div className="fit-column gap-m">
              {getPassportEntries(details?.adviserDoc).map((passport, index) => (
                <div key={passport.id || index} className="fit-column gap-m">
                  <div className="FP_Body_Semibold">{passport.holderName || `Passport ${index + 1}`}</div>
                  <div className="smGrid2">
                    <Row label="Name" value={passport.holderName || "-"} />
                    <Row label="Relationship / Role" value={passport.relationship || "-"} />
                    <Row label="Passport expiry date" value={fmtDate(passport.expiryDate)} />
                  </div>
                  {passport.images?.length ? <UploadedFileList files={passport.images} removable={false} />
                    : <p className="Content-Small muted">No passport attachments uploaded.</p>}
                </div>
              ))}
              {!getPassportEntries(details?.adviserDoc).length ? <p className="Content-Small muted">No passports added.</p> : null}
            </div>
          </PostCard>

          <PostCard title="Next of kin">
            <div className="smGrid2">
              <Row label="Name" value={details.nextOfKinDetails?.name} />
              <Row
                label="Relationship"
                value={details.nextOfKinDetails?.relationship}
              />
              <Row
                label="Email"
                value={details.nextOfKinDetails?.email}
                href={`mailto:${details.nextOfKinDetails?.email}`}
              />
              <Row
                label="Telephone"
                value={formatUKPhone(details.nextOfKinDetails?.telephone)}
                href={`tel:${formatUKPhone(details.nextOfKinDetails?.telephone)}`}
              />
              <Row label="Address" value={details.nextOfKinDetails?.address} />
              <Row label="Town" value={details.nextOfKinDetails?.town} />
              <Row label="County" value={details.nextOfKinDetails?.county} />
              <Row
                label="Postcode"
                value={details.nextOfKinDetails?.postcode}
              />
            </div>
          </PostCard>

          {adviserAdmins.length ? (
            <PostCard title="Adviser admins">
              <ul className="smAdviserAdminList">
                {adviserAdmins.map((admin, index) => {
                  const value = String(admin).trim();
                  const isEmail = value.includes("@");

                  return (
                    <li
                      className="FP_Body text-black"
                      key={`${value}-${index}`}
                    >
                      {isEmail ? (
                        <a href={`mailto:${value}`}>{value}</a>
                      ) : (
                        value
                      )}
                    </li>
                  );
                })}
              </ul>
            </PostCard>
          ) : null}
        </>
      )}
    </div>
  );
};

export default AdviserDetails;
